
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';

const BUTTON_POS_KEY = 'wacalls_fab_position';

const CAPTURE_WORKLET_CODE = `
class CaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (channel && channel.length) this.port.postMessage(channel.slice(0));
    return true;
  }
}
registerProcessor('capture-processor', CaptureProcessor);
`;

const PLAYBACK_WORKLET_CODE = `
const RING_SIZE = 16000 * 2;
class PlaybackProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.ring = new Float32Array(RING_SIZE);
    this.read = 0; this.write = 0; this.available = 0;
    this.port.onmessage = (e) => {
      const data = e.data;
      for (let i = 0; i < data.length; i++) {
        this.ring[this.write] = data[i];
        this.write = (this.write + 1) % RING_SIZE;
        if (this.available < RING_SIZE) this.available++;
        else this.read = (this.read + 1) % RING_SIZE;
      }
    };
  }
  process(_inputs, outputs) {
    const out = outputs[0][0];
    if (!out) return true;
    for (let i = 0; i < out.length; i++) {
      if (this.available > 0) {
        out[i] = this.ring[this.read];
        this.read = (this.read + 1) % RING_SIZE;
        this.available--;
      } else out[i] = 0;
    }
    return true;
  }
}
registerProcessor('playback-processor', PlaybackProcessor);
`;

function float32ToInt16LE(pcm) {
  const view = new DataView(new ArrayBuffer(pcm.length * 2));
  for (let i = 0; i < pcm.length; i++) {
    let s = pcm[i];
    if (isNaN(s)) s = 0; else if (s > 1) s = 1; else if (s < -1) s = -1;
    view.setInt16(i * 2, s < 0 ? Math.round(s * 32768) : Math.round(s * 32767), true);
  }
  return view.buffer;
}
function int16LEToFloat32(buf) {
  const view = new DataView(buf);
  const n = Math.floor(buf.byteLength / 2);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) out[i] = view.getInt16(i * 2, true) / 32768;
  return out;
}

function getClientId() {
  let id = localStorage.getItem('wacalls_client_id');
  if (!id) { id = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('wacalls_client_id', id); }
  return id;
}

let _captureUrl = null;
let _playbackUrl = null;
function getWorkletUrls() {
  if (!_captureUrl) {
    _captureUrl  = URL.createObjectURL(new Blob([CAPTURE_WORKLET_CODE],  { type: 'application/javascript' }));
    _playbackUrl = URL.createObjectURL(new Blob([PLAYBACK_WORKLET_CODE], { type: 'application/javascript' }));
  }
  return { captureUrl: _captureUrl, playbackUrl: _playbackUrl };
}

const WACALLS_BASE = 'https://wacalls.tendix.com.br';

// O acesso ao WaCalls usa o login do próprio CRM (validado pelo nginx junto ao backend).
function getCrmToken() {
  try {
    const raw = localStorage.getItem('token');
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (_) { return raw; }
  } catch (_) { return null; }
}

async function waCallsPost(path, body, authToken) {
  const r = await fetch(WACALLS_BASE + path, {
    method: 'POST',
    headers: { 'X-Client-Id': getClientId(), 'Content-Type': 'application/json', 'Authorization': `Bearer ${getCrmToken() || authToken}` },
    body: JSON.stringify(body || {})
  });
  if (!r.ok) { const t = await r.text().catch(() => ''); throw new Error(path + ' ' + r.status + ' ' + t); }
  return r.json().catch(() => ({}));
}

async function waCallsDelete(path, authToken) {
  const r = await fetch(WACALLS_BASE + path, {
    method: 'DELETE',
    headers: { 'X-Client-Id': getClientId(), 'Content-Type': 'application/json', 'Authorization': `Bearer ${getCrmToken() || authToken}` }
  });
  if (!r.ok) throw new Error(path + ' ' + r.status);
}

async function openWebRTCCall(sid, callId, authToken) {
  const { captureUrl, playbackUrl } = getWorkletUrls();

  const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });

  const pc = new RTCPeerConnection({ iceServers: [] });
  const dc = pc.createDataChannel('pcm', { ordered: true });
  dc.binaryType = 'arraybuffer';

  const ctx = new AudioContext({ sampleRate: 16000 });
  await ctx.audioWorklet.addModule(captureUrl);
  await ctx.audioWorklet.addModule(playbackUrl);
  await ctx.resume();

  const micSource = ctx.createMediaStreamSource(micStream);
  const captureNode = new AudioWorkletNode(ctx, 'capture-processor');
  captureNode.port.onmessage = (e) => {
    if (dc.readyState === 'open') dc.send(float32ToInt16LE(e.data));
  };
  micSource.connect(captureNode);
  captureNode.connect(ctx.destination);

  const playbackNode = new AudioWorkletNode(ctx, 'playback-processor');
  const streamDest = ctx.createMediaStreamDestination();
  playbackNode.connect(streamDest);

  const remoteAudio = new Audio();
  remoteAudio.srcObject = streamDest.stream;
  remoteAudio.play().catch(() => {});

  dc.onmessage = (e) => playbackNode.port.postMessage(int16LEToFloat32(e.data));

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  await new Promise(resolve => {
    if (pc.iceGatheringState === 'complete') return resolve();
    pc.addEventListener('icegatheringstatechange', () => {
      if (pc.iceGatheringState === 'complete') resolve();
    });
  });

  const { sdp_answer } = await waCallsPost(
    `/api/sessions/${sid}/calls/${callId}/webrtc`,
    { sdp_offer: pc.localDescription.sdp },
    authToken
  );
  await pc.setRemoteDescription({ type: 'answer', sdp: sdp_answer });

  return {
    close: () => {
      try { micStream.getTracks().forEach(t => t.stop()); } catch {}
      try { ctx.close(); } catch {}
      try { pc.close(); } catch {}
      try { remoteAudio.pause(); remoteAudio.srcObject = null; } catch {}
    }
  };
}

const useStyles = makeStyles((theme) => ({
  fab: {
    position: 'fixed',
    zIndex: 9998,
    cursor: 'grab',
    touchAction: 'none',
    userSelect: 'none',
    '&:active': { cursor: 'grabbing' }
  },
  fabDefault: {
    bottom: 25,
    right: 25,
  },
  fabBtn: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: theme.shadows[4],
    transition: 'box-shadow 0.2s, transform 0.15s',
    cursor: 'pointer',
    '&:hover': { boxShadow: theme.shadows[8], transform: 'scale(1.06)' },
    '&:focus': { outline: 'none' },
  },
  panel: {
    position: 'fixed',
    zIndex: 9999,
    width: 300,
    backgroundColor: '#fff',
    borderRadius: 14,
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    border: '1px solid rgba(0,0,0,0.08)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  panelHeader: {
    backgroundColor: '#25D366',
    color: '#fff',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 700,
    fontSize: 14,
    userSelect: 'none',
    '& button': { marginLeft: 'auto', background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18, lineHeight: 1 }
  },
  dialpad: { padding: 16, display: 'flex', flexDirection: 'column', gap: 8 },
  input: {
    width: '100%',
    padding: '8px 12px',
    fontSize: 20,
    textAlign: 'center',
    border: '2px solid #e0e0e0',
    borderRadius: 8,
    outline: 'none',
    letterSpacing: 2,
    '&:focus': { borderColor: '#25D366' },
    boxSizing: 'border-box',
    marginBottom: 8
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 },
  key: {
    padding: '12px 0',
    fontSize: 18,
    fontWeight: 600,
    border: '1px solid #e8e8e8',
    borderRadius: 8,
    cursor: 'pointer',
    backgroundColor: '#f7f7f7',
    transition: 'background 0.1s',
    '&:hover': { backgroundColor: '#e8f5e9' },
    '&:focus': { outline: 'none' },
    textAlign: 'center'
  },
  callBtn: {
    padding: '12px 0',
    fontSize: 22,
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontWeight: 700,
    transition: 'background 0.15s, transform 0.1s',
    '&:hover': { filter: 'brightness(1.1)', transform: 'scale(1.02)' },
    '&:focus': { outline: 'none' }
  },
  loginBtn: {
    width: '100%',
    padding: '12px 0',
    backgroundColor: '#25D366',
    color: '#fff', 
    border: 'none',
    borderRadius: 8,
    fontWeight: 700,
    fontSize: 16,
    cursor: 'pointer',
    '&:hover': { filter: 'brightness(1.1)' }
  },
  incomingModal: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99999
  },
  incomingCard: {
    backgroundColor: '#fff',
    borderRadius: 18,
    padding: 32,
    minWidth: 280,
    textAlign: 'center',
    boxShadow: '0 16px 48px rgba(0,0,0,0.25)',
  },
  callStatus: {
    position: 'fixed',
    zIndex: 9999,
    width: 300,
    backgroundColor: '#fff',
    borderRadius: 14,
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12
  }
}));

const WaCallsNative = ({ sessionId, authToken: propAuthToken }) => {
  const classes = useStyles();
  const [authToken, setAuthToken] = useState(() => getCrmToken());
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState('');
  const [callState, setCallState] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const connRef = useRef(null);
  const esRef = useRef(null);
  const timerRef = useRef(null);

  // Position & drag state
  const [buttonPos, setButtonPos] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const didDragRef = useRef(false);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    // login separado do WaCalls foi substituído pelo login do CRM
    setAuthToken(getCrmToken());
  }, [propAuthToken]);

  // Load saved position
  useEffect(() => {
    try {
      const saved = localStorage.getItem(BUTTON_POS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed.x === 'number' && typeof parsed.y === 'number') {
          setButtonPos(parsed);
        }
      }
    } catch (_) {}
  }, []);

  // Mouse / Touch Drag handlers
  const handleMouseDown = (e) => {
    if (e.button !== 0 && e.type !== 'touchstart') return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const target = document.getElementById('wacalls_native_fab');
    if (!target) return;
    const rect = target.getBoundingClientRect();
    if (!buttonPos) {
      setButtonPos({ x: rect.left, y: rect.top });
    }
    setIsDragging(true);
    setDragOffset({ x: clientX - rect.left, y: clientY - rect.top });
    didDragRef.current = false;
  };

  useEffect(() => {
    if (!isDragging) return;
    const handleMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const target = document.getElementById('wacalls_native_fab');
      if (!target) return;

      const maxX = window.innerWidth - target.offsetWidth;
      const maxY = window.innerHeight - target.offsetHeight;
      let newX = clientX - dragOffset.x;
      let newY = clientY - dragOffset.y;
      newX = Math.max(0, Math.min(newX, maxX));
      newY = Math.max(0, Math.min(newY, maxY));
      setButtonPos({ x: newX, y: newY });
      didDragRef.current = true;
    };

    const handleUp = () => {
      setIsDragging(false);
      setButtonPos((pos) => {
        if (pos) {
          try { localStorage.setItem(BUTTON_POS_KEY, JSON.stringify(pos)); } catch (_) {}
        }
        return pos;
      });
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleUp);
    };
  }, [isDragging, dragOffset]);

  // Window resize bounds
  useEffect(() => {
    if (!buttonPos) return;
    const onResize = () => {
      const target = document.getElementById('wacalls_native_fab');
      if (!target) return;
      const maxX = window.innerWidth - target.offsetWidth;
      const maxY = window.innerHeight - target.offsetHeight;
      let { x, y } = buttonPos;
      let changed = false;
      if (x > maxX) { x = maxX; changed = true; }
      if (y > maxY) { y = maxY; changed = true; }
      if (x < 0) { x = 0; changed = true; }
      if (y < 0) { y = 0; changed = true; }
      if (changed) setButtonPos({ x, y });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [buttonPos]);

  useEffect(() => {
    if (!sessionId || !authToken) return;
    const clientId = getClientId();
    const url = `${WACALLS_BASE}/api/events?clientId=${encodeURIComponent(clientId)}&t=${encodeURIComponent(getCrmToken() || '')}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.onmessage = (ev) => {
      let parsed;
      try { parsed = JSON.parse(ev.data); } catch { return; }

      // STRIKT ISOLATION: Ignore events from other sessions/numbers
      if (parsed.sessionId && sessionId && parsed.sessionId !== sessionId) {
        return;
      }

      if (parsed.type === 'incoming') {
        setCallState({ callId: parsed.id, sid: parsed.sessionId, peer: parsed.peer, status: 'incoming' });
      } else if (parsed.type === 'call-status') {
        if (parsed.status === 'connected') {
          setCallState(s => s ? { ...s, status: 'active' } : s);
          setSeconds(0);
          if (!timerRef.current) timerRef.current = setInterval(() => setSeconds(x => x + 1), 1000);
        }
      } else if (parsed.type === 'call-ended') {
        handleHangupLocal();
      } else if (parsed.type === 'incoming-claimed') {
        setCallState(s => (s?.callId === parsed.id ? null : s));
      } else if (false && parsed.type === 'auth-state' && parsed.state === 'unauthorized') {
        localStorage.removeItem('wacalls_auth_token');
        setAuthToken(null);
      }
    };
    es.onerror = () => {};

    return () => { es.close(); esRef.current = null; };
  }, [sessionId, authToken]);

  const handleHangupLocal = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (connRef.current) { connRef.current.close(); connRef.current = null; }
    setCallState(null);
    setSeconds(0);
  }, []);

  const handleDial = async () => {
    if (!number.trim() || !sessionId || !authToken) return;
    try {
      const res = await waCallsPost(`/api/sessions/${sessionId}/calls`, { phone: number, duration_ms: 300000, record: false }, authToken);
      const callId = res?.call?.callId;
      if (!callId) throw new Error('no callId');
      setCallState({ callId, sid: sessionId, peer: number, status: 'dialing' });
      const conn = await openWebRTCCall(sessionId, callId, authToken);
      connRef.current = conn;
    } catch (err) {
      console.error('[WaCalls Native] dial error:', err);
      alert('Erro ao iniciar chamada: ' + err.message);
      setCallState(null);
    }
  };

  const handleAnswer = async () => {
    if (!callState) return;
    try {
      await waCallsPost(`/api/sessions/${callState.sid}/calls/${callState.callId}/accept`, {}, authToken);
      const conn = await openWebRTCCall(callState.sid, callState.callId, authToken);
      connRef.current = conn;
      setCallState(s => ({ ...s, status: 'active' }));
      setSeconds(0);
      if (!timerRef.current) timerRef.current = setInterval(() => setSeconds(x => x + 1), 1000);
    } catch (err) {
      console.error('[WaCalls Native] answer error:', err);
      handleHangupLocal();
    }
  };

  const handleReject = async () => {
    if (!callState) return;
    try {
      if (callState.status === 'incoming') {
        await waCallsPost(`/api/sessions/${callState.sid}/calls/${callState.callId}/reject`, {}, authToken);
      } else {
        await waCallsDelete(`/api/sessions/${callState.sid}/calls/${callState.callId}`, authToken);
      }
    } catch {}
    handleHangupLocal();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoggingIn(true);
    try {
      const res = await fetch(`${WACALLS_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      if (!res.ok) throw new Error('Credenciais inválidas');
      const data = await res.json();
      localStorage.setItem('wacalls_auth_token', data.token);
      setAuthToken(data.token);
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoggingIn(false);
    }
  };

  const addKey = (k) => setNumber(n => (n + k).slice(0, 20));
  const delKey = () => setNumber(n => n.slice(0, -1));
  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2,'0')}:${String(s % 60).padStart(2,'0')}`;
  const KEYS = ['1','2','3','4','5','6','7','8','9','*','0','#'];

  if (!sessionId) return null;

  // Calculate panel position near the button
  const getPanelStyle = () => {
    if (!buttonPos) return { bottom: 80, right: 25 };
    const panelW = 300;
    const panelH = 430;
    let left = buttonPos.x - panelW + 48;
    let top = buttonPos.y - panelH - 10;
    if (left < 10) left = 10;
    if (left + panelW > window.innerWidth - 10) left = window.innerWidth - panelW - 10;
    if (top < 10) top = buttonPos.y + 58; // if too close to top, open below
    return { left, top };
  };

  return (
    <>
      {!callState && (
        <div
          id="wacalls_native_fab"
          className={`${classes.fab} ${!buttonPos ? classes.fabDefault : ''}`}
          style={buttonPos ? { left: buttonPos.x, top: buttonPos.y } : undefined}
          onMouseDown={handleMouseDown}
          onTouchStart={handleMouseDown}
          title="Telefone WaCalls (Arraste para mover)"
        >
          <button
            type="button"
            className={classes.fabBtn}
            style={{ backgroundColor: '#25D366', color: '#fff' }}
            onClick={() => {
              if (didDragRef.current) {
                didDragRef.current = false;
                return;
              }
              setOpen(o => !o);
            }}
            aria-label="Abrir WaCalls Webphone"
          >
            <span style={{ fontSize: 22 }}>📞</span>
          </button>
        </div>
      )}

      {open && !callState && (
        <div className={classes.panel} style={getPanelStyle()}>
          <div className={classes.panelHeader}>
            WaCalls Webphone
            <button onClick={() => setOpen(false)} title="Fechar">✕</button>
          </div>
          
          {!authToken ? (
            <div style={{ padding: 20 }}>
              <p style={{ margin: '0 0 16px 0', fontSize: 14, color: '#555', textAlign: 'center' }}>
                Entre com sua conta WaCalls para começar a fazer chamadas pelo CRM.
              </p>
              <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <input 
                  type="email" 
                  placeholder="Email WaCalls" 
                  className={classes.input} 
                  style={{ fontSize: 14, textAlign: 'left', padding: '10px 12px' }}
                  value={loginEmail} 
                  onChange={e => setLoginEmail(e.target.value)} 
                  required 
                />
                <input 
                  type="password" 
                  placeholder="Senha" 
                  className={classes.input} 
                  style={{ fontSize: 14, textAlign: 'left', padding: '10px 12px' }}
                  value={loginPassword} 
                  onChange={e => setLoginPassword(e.target.value)} 
                  required 
                />
                {loginError && <div style={{ color: 'red', fontSize: 12, textAlign: 'center' }}>{loginError}</div>}
                <button type="submit" className={classes.loginBtn} disabled={loggingIn}>
                  {loggingIn ? 'Entrando...' : 'Entrar no WaCalls'}
                </button>
              </form>
            </div>
          ) : (
            <div className={classes.dialpad}>
              <input
                className={classes.input}
                value={number}
                onChange={e => setNumber(e.target.value.replace(/[^0-9+*#]/g, '').slice(0, 20))}
                placeholder="(00) 00000-0000"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') handleDial(); }}
              />
              <div className={classes.grid}>
                {KEYS.map(k => (
                  <button key={k} className={classes.key} onClick={() => addKey(k)}>{k}</button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className={classes.callBtn} style={{ flex: 1, backgroundColor: '#25D366', color: '#fff' }} onClick={handleDial} disabled={!number.trim()}>📞</button>
                <button className={classes.callBtn} style={{ backgroundColor: '#f0f0f0', color: '#333', width: 48 }} onClick={delKey}>⌫</button>
              </div>
              <button 
                onClick={() => { localStorage.removeItem('wacalls_auth_token'); setAuthToken(null); }} 
                style={{ background: 'none', border: 'none', color: '#999', fontSize: 12, cursor: 'pointer', marginTop: 10 }}>
                Sair da conta
              </button>
            </div>
          )}
        </div>
      )}

      {callState && callState.status !== 'incoming' && (
        <div className={classes.callStatus} style={getPanelStyle()}>
          <div style={{ fontSize: 36 }}>{callState.status === 'active' ? '🔊' : '📡'}</div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{callState.peer}</div>
          <div style={{ color: '#666', fontSize: 13 }}>
            {callState.status === 'active' ? fmtTime(seconds) : 'Conectando...'}
          </div>
          <button className={classes.callBtn} style={{ backgroundColor: '#f44336', color: '#fff', padding: '10px 32px', borderRadius: 24, fontSize: 20 }} onClick={handleReject}>📵</button>
        </div>
      )}

      {callState && callState.status === 'incoming' && (
        <div className={classes.incomingModal}>
          <div className={classes.incomingCard}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>📲</div>
            <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 4 }}>Ligação Recebida</div>
            <div style={{ color: '#555', marginBottom: 24 }}>{callState.peer}</div>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
              <button className={classes.callBtn} style={{ backgroundColor: '#f44336', color: '#fff', padding: '12px 28px', borderRadius: 28, fontSize: 22 }} onClick={handleReject}>📵</button>
              <button className={classes.callBtn} style={{ backgroundColor: '#25D366', color: '#fff', padding: '12px 28px', borderRadius: 28, fontSize: 22 }} onClick={handleAnswer}>📞</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WaCallsNative;
